/**
 * ReportFeedbackButton — the app-wide "Report Feedback" control.
 * SPEC-stakeholder-portal-2026-06-23, R3 (the "three surfaces" #1).
 *
 * Mounted in DashboardLayout's header, so it is available on EVERY authenticated
 * screen, to EVERY tenant + role (this is intentionally the one surface everyone
 * gets — the gated portal in R5 is separate). It opens a small form: the
 * reporter's words + the CURRENT route, auto-captured from react-router-dom v6
 * (useLocation) — the reporter never types where they are.
 *
 * On submit it posts ONE untriaged feedback row (no GitHub issue is created — that
 * happens later at triage, decision D1 / R6) and shows a DURABLE RECEIPT: a toast
 * that links to "Your Issues", where the item is already visible as 📥 Received
 * (via R1's tenant privacy read). Not a black hole.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MessageSquarePlus, ImagePlus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { portalWriteError } from '@/lib/portal/mutationError';
import { useAuth } from '@/lib/auth/AuthProvider';
import { usePortalPanel } from '@/lib/portal/PortalPanelContext';
import { PANEL_SUBMISSIONS_QUERY_KEY } from '@/components/portal/panel/MySubmissionsTab';

// The query key the "Your Issues" list uses — exported so the page and this
// control agree on one key (submit invalidates it so the receipt shows at once).
export const YOUR_ISSUES_QUERY_KEY = ['feedback', 'mine'] as const;

// The panel's My Submissions query key (shared with MySubmissionsTab — R3) — a
// portal-enabled reporter's receipt opens the panel, so its list is invalidated
// too so the new 📥 Received item is present the instant the panel opens.
export const PORTAL_ISSUES_QUERY_KEY = PANEL_SUBMISSIONS_QUERY_KEY;

// The per-user capability that makes the hardened Stakeholder Portal the ONE
// canonical status surface a reporter lands on after a report (R1 AC6).
const STAKEHOLDER_PORTAL_CAP = 'stakeholder_portal';

// R4 AC1 / R5 AC4: the OPTIONAL image-attach bounds. Pinned here to match the
// backend's ALLOWED_IMAGE_TYPES + MAX_IMAGE_SIZE_MB + MAX_IMAGES_PER_REPORT
// (routers/feedback.py) — the client rejects an out-of-bounds file (or a
// selection past the cap) up front with a clear message so the reporter never
// round-trips a 400.
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
// R5 HIGH-3 fix: the backend has accepted up to 5 images per report since R5
// AC4, but no client could ever exercise more than one. Pinned to match
// routers/feedback.py::MAX_IMAGES_PER_REPORT.
const MAX_IMAGES_PER_REPORT = 5;

interface ReportFeedbackButtonProps {
  /** Controlled open state. When provided (with onOpenChange) the dialog is driven
   *  externally — used by a page that triggers feedback from its OWN cta (e.g. the
   *  standalone /roadmap page, which has no header to mount the built-in button). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button (for controlled/external-trigger use). */
  hideTrigger?: boolean;
}

export function ReportFeedbackButton({
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: ReportFeedbackButtonProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { openPanel } = usePortalPanel();
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = (value: boolean) => {
    if (onOpenChange) onOpenChange(value);
    else setLocalOpen(value);
  };
  const [body, setBody] = useState('');
  // R5 (HIGH-3): up to MAX_IMAGES_PER_REPORT OPTIONAL attached images + a
  // client-side validation message (was a single `image: File | null`).
  const [images, setImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Finding 5: a per-dialog-session idempotency key. Stable across a double-fire
  // (double-click / retry) so a concurrent double-submit collapses to ONE feedback
  // row at the DB (V193 partial unique index). A fresh dialog session mints a new
  // key, so a later re-report of the same text is never blocked.
  const submitKeyRef = useRef<string>('');
  // F4: the body value at the moment of the last submit ERROR (null when there is no
  // outstanding error). While non-null, the NEXT body edit that actually changes the
  // text mints a fresh submit_key -- so an unedited network-drop retry keeps the same
  // key (the DB submit_key guard dedupes it) but a genuinely edited resubmit gets a
  // distinct key (no silent drop of the edited content).
  const lastErrorBodyRef = useRef<string | null>(null);
  const freshSubmitKey = () => {
    submitKeyRef.current =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `sk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return submitKeyRef.current;
  };
  // Mint a fresh idempotency key whenever the dialog OPENS — covers both the
  // built-in trigger and an external/controlled open (e.g. the /roadmap CTA),
  // so an externally-opened dialog never submits with a stale/empty submit_key.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) freshSubmitKey();
    prevOpenRef.current = open;
  }, [open]);
  const handleBodyChange = (value: string) => {
    if (lastErrorBodyRef.current !== null && value.trim() !== lastErrorBodyRef.current.trim()) {
      // The reporter edited the text after an error -> this is a genuinely different
      // resubmit; give it a fresh idempotency key and clear the error baseline.
      freshSubmitKey();
      lastErrorBodyRef.current = null;
    }
    setBody(value);
  };

  // AC6 (R1) / R3 AC6: the portal is the canonical surface. A portal-enabled
  // reporter's receipt now OPENS the header panel (pre-selected to My
  // Submissions) instead of navigating to the retired /stakeholder-portal
  // route; a reporter WITHOUT the capability is unaffected and still lands
  // on the minimal /your-issues receipt list.
  const portalEnabled = typeof can === 'function' && can(STAKEHOLDER_PORTAL_CAP);
  const receiptLabel = portalEnabled ? 'Your Submissions' : 'Your Issues';
  const openReceipt = () => {
    if (portalEnabled) {
      openPanel('my_submissions');
    } else {
      navigate('/your-issues');
    }
  };

  // Auto-captured, never typed: the route the reporter is on right now.
  const route = `${location.pathname}${location.search}`;

  // R5 AC4 / HIGH-3: validate EACH newly-chosen file client-side (type + 5MB)
  // and surface a clear message; an invalid file is REJECTED (not attached) so
  // the reporter fixes it before submitting — the text is untouched. Valid
  // files are ADDED to the existing selection (multi-select, or repeated
  // single-file picks), capped at MAX_IMAGES_PER_REPORT with a clear message
  // when the selection would exceed it (only the first N are kept).
  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const errors: string[] = [];
    const accepted: File[] = [];
    for (const file of files) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: please choose a PNG, JPEG, or WebP image.`);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        errors.push(`${file.name}: that image is too large. Maximum size is ${MAX_IMAGE_SIZE_MB} MB.`);
        continue;
      }
      accepted.push(file);
    }

    setImages((prev) => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_IMAGES_PER_REPORT) {
        errors.push(
          `You can attach up to ${MAX_IMAGES_PER_REPORT} images. Only the first ${MAX_IMAGES_PER_REPORT} were kept.`,
        );
      }
      return combined.slice(0, MAX_IMAGES_PER_REPORT);
    });
    setImageError(errors.length > 0 ? errors.join(' ') : null);
    // Reset the input so re-choosing the SAME file after removing it fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImageError(null);
  };

  const clearImages = () => {
    setImages([]);
    setImageError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitMutation = useMutation({
    mutationFn: () =>
      api.feedback.create({
        body: body.trim(),
        route,
        // A human label for the screen — the pathname is a reasonable default;
        // R5's portal can render something friendlier later.
        source_screen: location.pathname,
        // Finding 5: reuse the session's key (set on open) so a double-fire shares
        // it; generate one lazily if the dialog was opened before this code shipped.
        submit_key: submitKeyRef.current || freshSubmitKey(),
        // Only include the images key when at least one is attached, so a
        // text-only report is byte-for-byte the same request it always was.
        ...(images.length > 0 ? { images } : {}),
      }),
    onSuccess: async () => {
      // Make the receipt real BEFORE the toast points at it: refetch the list(s) so
      // the new 📥 Received item is already present when the user follows the link.
      // Both keys are invalidated so whichever surface the reporter lands on (the
      // canonical portal for a portal-enabled client, else the minimal receipt) is
      // fresh (AC6).
      await queryClient.invalidateQueries({ queryKey: YOUR_ISSUES_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: PORTAL_ISSUES_QUERY_KEY });
      toast.success('Thanks, we got your feedback.', {
        description: `Track it under ${receiptLabel}.`,
        action: {
          label: receiptLabel,
          onClick: openReceipt,
        },
      });
      setBody('');
      clearImages();
      // Finding 5: retire this session's key so the next report gets a fresh one.
      submitKeyRef.current = '';
      // F4: a clean submit clears any prior-error state so the next report is fresh.
      lastErrorBodyRef.current = null;
      setOpen(false);
      // F3 (AC6 — the headline miss) / R3 AC6: the portal is the CANONICAL status
      // surface, so a portal-enabled reporter must actually LAND on their receipt
      // after a report, not merely be OFFERED a toast link. Open the panel directly
      // (no navigation — this control already lives inside the SAME DashboardLayout
      // header as the panel trigger, so the panel opens right where the reporter
      // is); the toast + its link stay too. A reporter WITHOUT the capability is
      // unaffected and stays where they are (the toast still links to /your-issues).
      if (portalEnabled) openPanel('my_submissions');
    },
    onError: (err: unknown) => {
      // The dialog stays OPEN (the user's typed text is preserved so they can retry)
      // and the Send button un-sticks (react-query clears isPending on error). ANY
      // non-2xx or network failure — including a CORS-stripped 500 — surfaces a
      // legible toast here rather than a silent stuck dialog.
      toast.error(portalWriteError(err, 'Could not submit feedback'));
      // F4 (data-loss window, refined): a submit can COMMIT at the DB yet still surface
      // a network error here. Minting a fresh key on EVERY error was wrong: a plain
      // network-drop RETRY of the SAME unedited text would then get a NEW key and create
      // a DUPLICATE report (the V193/V199 submit_key unique guard could no longer dedupe
      // it). Instead, KEEP the same key on error and only mint a fresh one when the
      // reporter actually EDITS the body afterwards (handled in the textarea onChange) --
      // so an unedited retry dedupes, while a genuinely different resubmit gets a
      // distinct key. Record the body that errored as the edit baseline.
      lastErrorBodyRef.current = body;
    },
  });

  const canSubmit = body.trim().length > 0 && !submitMutation.isPending;

  return (
    <>
      {!hideTrigger && (
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={() => {
            // Finding 5: mint a fresh idempotency key for this report session.
            freshSubmitKey();
            setOpen(true);
          }}
          aria-label="Report feedback"
        >
          <MessageSquarePlus className="h-4 w-4" />
          <span className="hidden sm:inline">Report Feedback</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Feedback</DialogTitle>
            <DialogDescription>
              Tell us what is not working or what you would like to see. We will track it
              and you can follow its progress under {receiptLabel}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="feedback-body">What is on your mind?</Label>
              <Textarea
                id="feedback-body"
                value={body}
                onChange={(e) => handleBodyChange(e.target.value)}
                placeholder="Describe the issue or request…"
                rows={5}
                autoFocus
              />
            </div>

            {/* R5 AC4 / HIGH-3: up to MAX_IMAGES_PER_REPORT OPTIONAL screenshots
                (PNG/JPEG/WebP, max 5 MB each). Was a single-image attach (R4 AC1);
                the backend has accepted up to 5 since R5, this UI now does too. */}
            <div className="space-y-2">
              <Label htmlFor="feedback-image">
                Attach screenshots (optional, up to {MAX_IMAGES_PER_REPORT})
              </Label>
              {/* A styled attach control instead of the raw native "Choose File / No
                  file chosen" widget: the real <input> is visually hidden (sr-only, so
                  it stays interactable for tests + keyboard) and a button-styled <label>
                  triggers it — matching the app's outline Button. */}
              <div>
                <label
                  htmlFor="feedback-image"
                  data-testid="feedback-image-label"
                  className={cn(
                    'inline-flex h-8 w-fit items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-500',
                    images.length >= MAX_IMAGES_PER_REPORT
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                  )}
                >
                  <ImagePlus className="h-4 w-4" />
                  {images.length > 0 ? 'Add more images' : 'Choose images'}
                </label>
                <input
                  id="feedback-image"
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_IMAGE_TYPES.join(',')}
                  onChange={handleFilesChange}
                  disabled={images.length >= MAX_IMAGES_PER_REPORT}
                  data-testid="feedback-image-input"
                  className="sr-only"
                />
              </div>
              {images.length > 0 && (
                <ul
                  className="space-y-1"
                  data-testid="feedback-images-selected"
                >
                  {images.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 text-xs text-muted-foreground"
                      data-testid="feedback-image-selected"
                    >
                      <ImagePlus className="h-3.5 w-3.5" />
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        aria-label={`Remove ${file.name}`}
                        data-testid={`feedback-image-remove-${index}`}
                        className="ml-auto inline-flex items-center rounded p-0.5 hover:bg-muted"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {imageError && (
                <p className="text-xs text-destructive" data-testid="feedback-image-error">
                  {imageError}
                </p>
              )}
            </div>

            <div className="text-xs text-muted-foreground">
              Reporting from: <span className="font-mono" data-testid="feedback-route">{route}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => submitMutation.mutate()} disabled={!canSubmit}>
              {submitMutation.isPending ? 'Sending…' : 'Send Feedback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
